'use client';

import type { FormEvent } from 'react';
import { useEffect, useState } from 'react';
import { resolveAppDestination } from '@ferty9/auth';
import { useRouter } from 'next/navigation';
import { getCurrentUser, login as loginRequest } from '@/lib/api/auth';
import { redirectToAdminApp } from '@/lib/api/public-config';
import { clearSurveySession, getSurveySession, setSurveySessionFromLogin } from '@/lib/storage/session';
import { SurveyLoginForm } from './SurveyLoginForm';
import { PasswordRecovery } from './PasswordRecovery';

export function SurveyLoginPage() {
  const router = useRouter();
  const [checkingSession, setCheckingSession] = useState(true);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [recovering, setRecovering] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const checkSession = async () => {
      const savedSession = getSurveySession();
      if (savedSession?.token) {
        const destination = resolveAppDestination({
          role: savedSession.role,
          is_superadmin: savedSession.is_superadmin,
          isRootAdmin: savedSession.isRootAdmin,
          permissions: savedSession.permissions,
        });
        if (destination === 'survey') {
          router.replace(`/survey${window.location.search}`);
          return;
        }
        clearSurveySession();
        if (destination === 'reports') {
          router.replace('/reports/dashboard');
          return;
        }
        if (destination === 'admin') {
          await redirectToAdminApp('/dashboard');
          return;
        }
      }

      try {
        const current = await getCurrentUser();
        if (cancelled) return;
        const role = current.user.role || (current.user.is_superadmin ? 'ADMIN' : 'REPORTS');
        const user = { ...current.user, role };
        const destination = resolveAppDestination(user);
        if (destination === 'admin') {
          await redirectToAdminApp('/dashboard');
          return;
        }
        if (destination === 'reports') {
          router.replace('/reports/dashboard');
          return;
        }
        if (destination === 'survey') {
          setSurveySessionFromLogin({ user: { ...current.user, role } });
          router.replace(`/survey${window.location.search}`);
          return;
        }
      } catch {
        clearSurveySession();
      }
      if (!cancelled) setCheckingSession(false);
    };

    void checkSession();
    return () => {
      cancelled = true;
    };
  }, [router]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    try {
      const response = await loginRequest({ username: username.trim(), password });
      if (response.user.password_reset_required) {
        clearSurveySession();
        setRecovering(true);
        return;
      }
      const role = response.user.role || (response.user.is_superadmin ? 'ADMIN' : 'REPORTS');
      const user = { ...response.user, role };
      const destination = resolveAppDestination(user);
      if (destination === 'admin') {
        await redirectToAdminApp('/dashboard');
        return;
      }
      if (destination === 'reports') {
        setSurveySessionFromLogin({ ...response, user });
        router.replace('/reports/dashboard');
        return;
      }
      if (destination !== 'survey') {
        setError('Access Denied: Your account is not assigned to a permitted app.');
        return;
      }
      setSurveySessionFromLogin({ ...response, user });
      router.replace(`/survey${window.location.search}`);
    } catch (loginError) {
      setError(loginError instanceof Error ? loginError.message : 'Server error');
    } finally {
      setLoading(false);
    }
  };

  if (checkingSession) return null;

  if (recovering) return <div id="login-overlay" className="overlay show"><div className="auth-shell auth-shell-web"><aside className="auth-brand-panel"><img src="/logo.png" alt="Ferty9"/><div><span className="auth-eyebrow">ACCOUNT CARE</span><h1>Let us help you return safely.</h1><p>Secure verification protects your centre workspace.</p></div></aside><PasswordRecovery onBack={()=>setRecovering(false)}/></div></div>;
  return (
    <SurveyLoginForm
      username={username}
      password={password}
      passwordVisible={passwordVisible}
      error={error}
      loading={loading}
      onUsernameChange={setUsername}
      onPasswordChange={setPassword}
      onTogglePassword={() => setPasswordVisible(value => !value)}
      onSubmit={handleSubmit}
      onForgotPassword={()=>setRecovering(true)}
    />
  );
}
